import {
  type Job,
  type JobTitle,
  type JobWithRelations,
  PayRatePeriod,
  type ProjectCopilotConfigurationV1Snapshot,
  ScreeningAction,
} from '@factoryfixinc/ats-interfaces';

import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { LocalJobData } from './local-job-data.type';
import type { WorkPerformed } from './location-context.utils';
import type {
  GeneratedJobDescriptionDocument,
  GeneratedScreenerQuestionsDocument,
} from '@/core/shared/job/job.type';
import type {
  LocalScreenerQuestion,
  ScreeningTypeWithSkip,
} from './types/local-screener-question.type';
import type { ProjectInterviewSettings } from './types/interview-scheduling-settings.type';

export const useCopilotActivationStore = defineStore(
  'copilot-activation',
  () => {
    const selectedProjectId = ref<number | null>(null);
    const selectedJobId = ref<number | null>(null);
    const selectedJob = ref<JobWithRelations<
      | 'jobTitle'
      | 'screenerQuestions'
      | 'jobQualifications'
      | 'jobResponsibilities'
      | 'jobTaxonomyKnowledgeDisciplineMaps'
      | 'jobTaxonomyMachineMaps'
      | 'jobStatusHistories'
    > | null>(null);
    const selectedJobTitle = ref<JobTitle | null>(null);

    // Job Description Generation
    const jobDescriptionDocumentId = ref('');
    const generatedJobDescriptionDocument = ref<GeneratedJobDescriptionDocument | null>(null);
    const jobDescriptionPollingIntervalId = ref<number | null>(null);
    const jobDescriptionPollingIntervalTime = 2000;
    const jobDescriptionPollingMaxAttempts = 150;
    const jobDescriptionPollingTriedAttempts = ref(0);
    const isGeneratingJobDescription = ref(false);
    const stopGeneratingJobDescriptionFlag = ref(false);

    // Screener Questions Generation
    const generatedScreenerQuestionsDocumentId = ref('');
    const generatedScreenerQuestionsDocument = ref<GeneratedScreenerQuestionsDocument | null>(null);
    const screenerQuestionsPollingIntervalId = ref<number | null>(null);
    const screenerQuestionsPollingIntervalTime = 2000;
    const screenerQuestionsPollingMaxAttempts = 150;
    const screenerQuestionsPollingTriedAttempts = ref(0);
    const isGeneratingScreenerQuestions = ref(false);
    const stopGeneratingScreenerQuestionsFlag = ref(false);

    const jobStatus = ref<Job['status'] | null>(null);

    const localJobData = ref<LocalJobData>({
      employerName: '',
      jobTitleId: null,
      displayTitle: null,
      employerAssigneeId: null,
      hireType: null,
      shift: null,
      startTime: null,
      endTime: null,
      description: '',
      rawDescription: '',
      payRateMin: null,
      payRateMax: null,
      payRateSalaryMin: null,
      payRateSalaryMax: null,
      payRatePeriod: PayRatePeriod.HOURLY,
      minimumApplicantsPerWeek: 0,
      hiresPerPosition: 0,
      street1: '',
      city: '',
      state: '',
      postalCode: '',
      locationContext: null,
      travelExpectations: null,
      anonymous: false,
    });

    // "Where is the work performed?" while the location context is still unresolved (null).
    // A resolved context implies the answer; this only tells "In the field, no second answer
    // yet" apart from the legacy worksite that a null context otherwise reads as.
    const workPerformedAnswer = ref<WorkPerformed>('worksite');

    // Job Description util
    const localJobDescription = ref<string>('');
    // Text of the travel paragraph we wrote into the description this session, while unedited.
    const insertedTravelDisclosure = ref<string | null>(null);
    // The editor is emptied while a description is being generated; this brings the previous
    // text back if the generation is stopped or fails.
    const descriptionBeforeGeneration = ref<{
      description: string;
      insertedTravelDisclosure: string | null;
    } | null>(null);

    // Skill Keywords
    const localKnowledgeDisciplines = ref<number[]>([]);
    const localMachines = ref<number[]>([]);
    const localCustomKnowledgeDisciplines = ref<string[]>([]);
    const localCustomMachines = ref<string[]>([]);
    const localBrandsPerMachine = ref<{ [key: number]: number[] }>({});

    // Candidate Screening
    const localScreeningAction = ref<ScreeningAction | null>(null);
    const localScreeningType = ref<ScreeningTypeWithSkip | null>(null);
    const localEmployerCareerSiteUrl = ref<string | null>(null);

    // Screener Questions
    const localScreenerQuestions = ref<LocalScreenerQuestion[]>([]);

    // Related data needed for the job flow
    const jobTitles = ref<JobTitle[]>([]);

    // use to run validation and know validations should run on all the fields
    const hasTriedToUpdateJob = ref(false);

    const scheduledUpdates = ref(false);

    // Active Outreach Enabled
    const activeOutreachEnabled = ref(false);

    // Project Copilot Configuration
    const projectCopilotConfiguration = ref<ProjectCopilotConfigurationV1Snapshot>({});
    const copilotConfigurationTemplateId = ref<number | undefined>(undefined);

    // Interview Automation Settings
    const localInterviewSettings = ref<ProjectInterviewSettings>();

    return {
      activeOutreachEnabled,
      copilotConfigurationTemplateId,
      descriptionBeforeGeneration,
      generatedJobDescriptionDocument,
      generatedScreenerQuestionsDocument,
      generatedScreenerQuestionsDocumentId,
      hasTriedToUpdateJob,
      insertedTravelDisclosure,
      isGeneratingJobDescription,
      isGeneratingScreenerQuestions,
      jobDescriptionDocumentId,
      jobDescriptionPollingIntervalId,
      jobDescriptionPollingIntervalTime,
      jobDescriptionPollingMaxAttempts,
      jobDescriptionPollingTriedAttempts,
      jobStatus,
      jobTitles,
      localBrandsPerMachine,
      localCustomKnowledgeDisciplines,
      localCustomMachines,
      localEmployerCareerSiteUrl,
      localInterviewSettings,
      localJobData,
      localJobDescription,
      localKnowledgeDisciplines,
      localMachines,
      localScreenerQuestions,
      localScreeningAction,
      localScreeningType,
      projectCopilotConfiguration,
      scheduledUpdates,
      screenerQuestionsPollingIntervalId,
      screenerQuestionsPollingIntervalTime,
      screenerQuestionsPollingMaxAttempts,
      screenerQuestionsPollingTriedAttempts,
      selectedJob,
      selectedJobId,
      selectedJobTitle,
      selectedProjectId,
      stopGeneratingJobDescriptionFlag,
      stopGeneratingScreenerQuestionsFlag,
      workPerformedAnswer,
    };
  },
  { persist: false },
);
